import { PageContainer, PageHeader } from '@/components/Atoms'
import { useAgreementSummary } from '@/hooks/queries/crt/useAgreement'
import { useMasterAgreementMappingsLastUpdated } from '@/hooks/queries/crt/useAgreementMappings'
import { useBudgetAllocationSummary } from '@/hooks/queries/crt/useBudgetAllocations'
import { useLatestCalculationRunAccrual } from '@/hooks/queries/crt/useCalculationRunAccruals'
import { useLatestDistributionMarginMix } from '@/hooks/queries/crt/useDistributionMarginMix'
import { useLatestIpAccrual } from '@/hooks/queries/crt/useIpAccruals'
import { useManualInvestmentsLastUpdated } from '@/hooks/queries/crt/useManualInvestments'
import { useLatestRapForecast } from '@/hooks/queries/crt/useRapForecasts'
import { useRetailerMarginsFmcLastUpdated } from '@/hooks/queries/crt/useRetailerMarginsFmc'
import { useRetailerMarginsNcLastUpdated } from '@/hooks/queries/crt/useRetailerMarginsNc'
import { useLatestSalesDealActuals } from '@/hooks/queries/crt/useSalesDealActuals'
import { useWholesalerMarginsFmcLastUpdated } from '@/hooks/queries/crt/useWholesalerMarginsFmc'
import { useWholesalerMarginsNcLastUpdated } from '@/hooks/queries/crt/useWholesalerMarginsNc'
import {
  toCrtPeriodState,
  toMonthlyImportPeriodState,
  toUpdatedAt,
} from '@/lib/utils/getLastUpdatedState'
import { Insights, type InsightItem } from '@/routes/_auth/_modules/_shared/-components/Insights'
import { crtPeriodToParams } from '@/lib/utils/crtPeriodParams'
import { formatDateOnly } from '@/utils/DateOnlyHelpers'
import { getDefaultImportPeriod } from '@/components/CreateCrtPeriodDetailsDialog'
import { createFileRoute } from '@tanstack/react-router'
import { startOfYear } from 'date-fns'
import { useTranslation } from 'react-i18next'

export const Route = createFileRoute('/_auth/_modules/crt/customer-investments/insights/')({
  component: CustomerInvestmentsInsight,
})

function CustomerInvestmentsInsight() {
  const { t } = useTranslation(['crt', 'common'])

  // The monthly actuals pages are addressed by period, and the current month is never actualised yet:
  // the insight opens the previous month's page, the import the current MOPE expects.
  const expectedPeriodParams = crtPeriodToParams(formatDateOnly(getDefaultImportPeriod()))

  const { data: agreementSummary } = useAgreementSummary()
  const { data: latestRapForecast } = useLatestRapForecast()
  const { data: latestSalesDealActuals } = useLatestSalesDealActuals()
  const { data: latestDistributionMarginMix } = useLatestDistributionMarginMix()
  const { data: latestIpAccrual } = useLatestIpAccrual()
  const { data: latestCalculationRunAccrual } = useLatestCalculationRunAccrual()
  const { data: budgetAllocationSummary } = useBudgetAllocationSummary()
  const { data: retailerMarginsFmcLastUpdated } = useRetailerMarginsFmcLastUpdated()
  const { data: retailerMarginsNcLastUpdated } = useRetailerMarginsNcLastUpdated()
  const { data: wholesalerMarginsFmcLastUpdated } = useWholesalerMarginsFmcLastUpdated()
  const { data: wholesalerMarginsNcLastUpdated } = useWholesalerMarginsNcLastUpdated()
  const { data: masterAgreementMappingsLastUpdated } = useMasterAgreementMappingsLastUpdated()
  const { data: manualInvestmentsLastUpdated } = useManualInvestmentsLastUpdated()

  const automatedInsights: InsightItem[] = [
    {
      name: t('route.crt.agreements', { ns: 'common' }),
      frequency: t('insights.frequency.monthly'),
      frequencySchedule: { type: 'monthly', dayOfMonth: 5 },
      frequencyTooltip: t('customerInvestmentsInsight.agreements.frequencyDetail'),
      updatedAt: toUpdatedAt(agreementSummary),
      description: t('customerInvestmentsInsight.agreements.linkText'),
      link: { to: '/crt/customer-investments/insights/agreements' },
      permissions: ['crt_agreements_view'],
    },
    {
      name: t('route.crt.rapForecasts', { ns: 'common' }),
      frequency: t('insights.frequency.monthly'),
      frequencyTooltip: t('customerInvestmentsInsight.rapForecasts.frequencyDetail'),
      crtPeriodState: toCrtPeriodState(latestRapForecast),
      description: t('customerInvestmentsInsight.rapForecasts.linkText'),
      link: latestRapForecast?.id
        ? {
            to: '/crt/customer-investments/insights/rap-forecasts/$year/$month',
            params: crtPeriodToParams(latestRapForecast.crtPeriod),
          }
        : { to: '/crt/customer-investments/insights/rap-forecasts' },
      permissions: ['crt_rap_forecasts_view'],
    },
    {
      name: t('route.crt.salesDealActuals', { ns: 'common' }),
      frequency: t('insights.frequency.monthly'),
      frequencyTooltip: t('customerInvestmentsInsight.salesDealActuals.frequencyDetail'),
      crtPeriodState: toMonthlyImportPeriodState(latestSalesDealActuals),
      description: t('customerInvestmentsInsight.salesDealActuals.linkText'),
      link: {
        to: '/crt/customer-investments/insights/sales-deal-actuals/$year/$month',
        params: expectedPeriodParams,
      },
      permissions: ['crt_sales_deal_actuals_view'],
    },
    {
      name: t('route.crt.distributionMarginMix', { ns: 'common' }),
      frequency: t('insights.frequency.monthly'),
      frequencyTooltip: t('customerInvestmentsInsight.distributionMarginMix.frequencyDetail'),
      crtPeriodState: toCrtPeriodState(latestDistributionMarginMix),
      description: t('customerInvestmentsInsight.distributionMarginMix.linkText'),
      link: latestDistributionMarginMix?.id
        ? {
            to: '/crt/customer-investments/insights/distribution-margin-mix/$year/$month',
            params: crtPeriodToParams(latestDistributionMarginMix.crtPeriod),
          }
        : { to: '/crt/customer-investments/insights/distribution-margin-mix' },
      permissions: ['crt_distribution_margin_mix_view'],
    },
    {
      name: t('route.crt.ipAccruals', { ns: 'common' }),
      frequency: t('insights.frequency.monthly'),
      frequencyTooltip: t('customerInvestmentsInsight.ipAccruals.frequencyDetail'),
      crtPeriodState: toMonthlyImportPeriodState(latestIpAccrual),
      description: t('customerInvestmentsInsight.ipAccruals.linkText'),
      link: {
        to: '/crt/customer-investments/insights/ip-accruals/$year/$month',
        params: expectedPeriodParams,
      },
      permissions: ['crt_ip_accruals_view'],
    },
    {
      name: t('route.crt.calculationRunAccruals', { ns: 'common' }),
      frequency: t('insights.frequency.monthly'),
      frequencyTooltip: t('customerInvestmentsInsight.calculationRunAccruals.frequencyDetail'),
      crtPeriodState: toMonthlyImportPeriodState(latestCalculationRunAccrual),
      description: t('customerInvestmentsInsight.calculationRunAccruals.linkText'),
      link: {
        to: '/crt/customer-investments/insights/calculation-run-accruals/$year/$month',
        params: expectedPeriodParams,
      },
      permissions: ['crt_calculation_run_accruals_view'],
    },
    {
      name: t('route.crt.budgetAllocations', { ns: 'common' }),
      frequency: t('insights.frequency.daily'),
      frequencySchedule: { type: 'daily' },
      updatedAt: toUpdatedAt(budgetAllocationSummary),
      description: t('customerInvestmentsInsight.budgetAllocations.linkText'),
      link: { to: '/crt/customer-investments/insights/budget-allocations' },
      permissions: ['crt_budget_allocations_view'],
    },
  ]

  const assumptions: InsightItem[] = [
    {
      name: t('route.crt.retailerMarginsFmc', { ns: 'common' }),
      description: t('customerInvestmentsInsight.retailerMarginsFmc.linkText'),
      updatedAt: toUpdatedAt(retailerMarginsFmcLastUpdated),
      link: { to: '/crt/customer-investments/insights/retailer-margins/fmc' },
      permissions: ['crt_retailer_margins_fmc_view'],
    },
    {
      name: t('route.crt.retailerMarginsNc', { ns: 'common' }),
      description: t('customerInvestmentsInsight.retailerMarginsNc.linkText'),
      updatedAt: toUpdatedAt(retailerMarginsNcLastUpdated),
      link: { to: '/crt/customer-investments/insights/retailer-margins/nc' },
      permissions: ['crt_retailer_margins_nc_view'],
    },
    {
      name: t('route.crt.wholesalerMarginsFmc', { ns: 'common' }),
      description: t('customerInvestmentsInsight.wholesalerMarginsFmc.linkText'),
      updatedAt: toUpdatedAt(wholesalerMarginsFmcLastUpdated),
      link: { to: '/crt/customer-investments/insights/wholesaler-margins/fmc' },
      permissions: ['crt_wholesaler_margins_fmc_view'],
    },
    {
      name: t('route.crt.wholesalerMarginsNc', { ns: 'common' }),
      description: t('customerInvestmentsInsight.wholesalerMarginsNc.linkText'),
      updatedAt: toUpdatedAt(wholesalerMarginsNcLastUpdated),
      link: { to: '/crt/customer-investments/insights/wholesaler-margins/nc' },
      permissions: ['crt_wholesaler_margins_nc_view'],
    },
    {
      name: t('route.crt.masterAgreements', { ns: 'common' }),
      description: t('customerInvestmentsInsight.masterAgreements.linkText'),
      updatedAt: toUpdatedAt(masterAgreementMappingsLastUpdated),
      link: {
        to: '/crt/customer-investments/insights/master-agreements',
        search: { validFrom: startOfYear(new Date()) },
      },
      permissions: ['crt_agreement_mappings_view'],
    },
    {
      name: t('route.crt.manualInvestments', { ns: 'common' }),
      description: t('customerInvestmentsInsight.manualInvestments.linkText'),
      updatedAt: toUpdatedAt(manualInvestmentsLastUpdated),
      link: { to: '/crt/customer-investments/insights/manual-investments' },
      permissions: ['crt_manual_investments_view'],
    },
  ]

  return (
    <PageContainer>
      <PageHeader>{t('customerInvestmentsInsight.pageHeader')}</PageHeader>
      <Insights automatedImports={automatedInsights} assumptions={assumptions} />
    </PageContainer>
  )
}
